import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { Plus } from '@lyearn/icons';
import { useNodePerformanceContext } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Autocomplete, Stack, Typography } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import WideCardLoader from '@/modules/content/components/ContentCard/Wide/Loader';
import { RenderInput } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/Search/renderers';
import { AutocompleteRenderOptionState } from '@/modules/corporate/modules/database/fields/components/DatabaseSelect/components/OptionItem/types';
import { useExercisesQuery } from '@/modules/corporate/modules/exercise/views/Review/graphql/queries/generated/exercises';
import { ExerciseType, OrderEnum, ResourceStatusEnum, ResourceTypeEnum } from '@/types/schema';
import dynamic from '@/utils/dynamic';

import { useCourse } from '../../courseContext';
import CourseEntity from '../../entity/Course';
import { renderOption } from './renderers';
import { ExerciseCardContainerProps, ExerciseOption } from './types';

const ExerciseContainerLoadable = dynamic(
  () =>
    import(
      /* webpackChunkName: "exercise-container-container" */ '@/modules/corporate/modules/exercise/components/ExerciseContainer'
    ),
);

interface ICreateOption {
  type: 'create';
}

type IOption = (ExerciseOption & { type?: undefined }) | ICreateOption;

const CREATE_OPTION: ICreateOption = { type: 'create' };

const getOptionLabel = (option: IOption) => (option.type === 'create' ? '' : option.title);

const isOptionEqualToValue = (option: IOption, value: IOption) => {
  if (option.type === 'create' || value.type === 'create') {
    return false;
  }
  return option.id === value.id;
};

type ExerciseCardProps = ExerciseCardContainerProps & {
  onCreateNew: () => void;
  onEditExercise?: () => void;
  inlinePlayer?: boolean;
};

const ExerciseCard: React.FC<ExerciseCardProps> = (props) => {
  const { t } = useTranslation();
  const {
    updateAttribute,
    exerciseId,
    editable,
    duration,
    id,
    onCreateNew,
    onEditExercise,
    inlinePlayer,
  } = props;
  const { setNodePerformance } = useNodePerformanceContext();
  const [inputValue, setValue, searchString] = useDebounceState('');
  const [selectedValue, setSelectedValue] = useState<IOption | undefined>(undefined);

  const { course } = useCourse();

  const [{ data }] = useExercisesQuery({
    variables: {
      filters: {
        name: searchString,
        types: [ResourceTypeEnum.Exercise],
        shareable: course.isSent ? true : undefined,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
      },
      sort: [{ field: 'relevance', order: OrderEnum.Desc }],
    },
    pause: !editable,
  });

  const options: IOption[] = useMemo(() => {
    const exerciseOptions: ExerciseOption[] = data?.contents.edges?.length
      ? data.contents.edges.map(({ node }) => {
          const exercise = node as ExerciseType;
          const { id, name } = exercise;
          return { id, title: name, exercise };
        })
      : [];
    return [CREATE_OPTION, ...exerciseOptions];
  }, [data?.contents.edges]);

  const onChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: IOption | null) => {
      if (!value) {
        return;
      }
      if (value.type === 'create') {
        onCreateNew();
        setSelectedValue(undefined);
        return;
      }
      setSelectedValue(value);
      updateAttribute({ exerciseId: value.id });
    },
    [onCreateNew, updateAttribute],
  );

  const onInputChange = useCallback(
    (_event: React.ChangeEvent<{}>, value: string) => {
      setValue(value);
    },
    [setValue],
  );

  const onLoad = useCallback(
    (exercise: any) => {
      if (editable && exercise.duration && exercise.duration !== duration) {
        updateAttribute({ duration: exercise.duration });
      }
    },
    [duration, editable, updateAttribute],
  );

  const setExercisePerformance = useCallback(() => {
    id && setNodePerformance?.({ id, blockType: 'exercise', progress: 100 });
  }, [id, setNodePerformance]);

  useEffect(() => {
    // set performance for empty block where exercise is still to be attach
    if (!editable && !exerciseId && !duration) {
      setExercisePerformance();
    }
  }, [duration, editable, exerciseId, setExercisePerformance]);

  // Server-side filtering via debouncedValue handles exercise search.
  // Pass all options through so the create option is always visible.
  const filterOptions = useCallback((opts: IOption[]) => opts, []);

  const optionLabelRenderer = useCallback(
    (option: IOption, _state: AutocompleteRenderOptionState) => {
      if (option.type === 'create') {
        return (
          <Stack alignItems="center" className="max-w-full" gapX={4}>
            <Plus className="h-16 w-16 flex-shrink-0 fill-current text-text-placeholder" />
            <Typography color="text-text-placeholder" variant="body-short-01">
              {t('Course:Builder.CreateExercise')}
            </Typography>
          </Stack>
        );
      }
      return renderOption(option);
    },
    [t],
  );

  const _renderInput = useCallback(
    (params: any) =>
      RenderInput({
        params,
        onBlurPlaceholder: t('Course:Builder.AttachExercise'),
        onFocusPlaceholder: t('Course:Builder.SearchForExercise'),
      }),
    [t],
  );

  if (!CourseEntity.id(course)) {
    return null;
  }

  return (
    <div className={cn('relative my-32', { 'pointer-events-none': !CourseEntity.id(course) })}>
      {exerciseId ? (
        <Suspense fallback={<WideCardLoader className="py-60" />}>
          <ExerciseContainerLoadable
            exerciseId={exerciseId}
            inlinePlayer={inlinePlayer}
            languageType={course.languageType}
            parentEntityId={CourseEntity.id(course)}
            onEditExercise={onEditExercise}
            onExerciseSubmit={setExercisePerformance}
            onLoad={onLoad}
          />
        </Suspense>
      ) : editable ? (
        <Autocomplete
          disableClearable
          disablePortal
          filterOptions={filterOptions}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          isOptionEqualToValue={isOptionEqualToValue}
          optionLabelRenderer={optionLabelRenderer}
          options={options}
          popupIcon={null}
          renderInput={_renderInput}
          size="large"
          value={selectedValue}
          onChange={onChange}
          onInputChange={onInputChange}
          onKeyDown={stopEventPropagation}
          onKeyPress={stopEventPropagation}
          onPaste={stopEventPropagation}
        />
      ) : (
        <div />
      )}
    </div>
  );
};

export default ExerciseCard;
